import { getDocumentPath } from './document-panel-paths.js';

export interface TocHeadingRef {
  level: number;
  text: string;
  pos: number;
  kind?: 'heading' | 'scene';
}

export interface PendingSectionScroll {
  title: string;
  level: number;
}

export const SECTION_TITLE_SEARCH_PARAM = 'sectionTitle';
export const SECTION_LEVEL_SEARCH_PARAM = 'sectionLevel';

export function buildDocumentSectionPath(
  projectId: string | undefined,
  documentId: string,
  section: PendingSectionScroll,
  existingSearch = '',
): string {
  const path = getDocumentPath(projectId, documentId);
  const normalizedSearch = existingSearch.startsWith('?') ? existingSearch.slice(1) : existingSearch;
  const params = new URLSearchParams(normalizedSearch);
  params.set(SECTION_TITLE_SEARCH_PARAM, section.title);
  params.set(SECTION_LEVEL_SEARCH_PARAM, String(section.level));
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

export function parseSectionScrollFromSearchParams(
  searchParams: URLSearchParams,
): PendingSectionScroll | null {
  const title = searchParams.get(SECTION_TITLE_SEARCH_PARAM);
  const levelRaw = searchParams.get(SECTION_LEVEL_SEARCH_PARAM);
  if (!title || !levelRaw) {
    return null;
  }
  const level = Number(levelRaw);
  if (!Number.isFinite(level)) {
    return null;
  }
  return { title, level };
}

export function stripSectionScrollSearchParams(searchParams: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(searchParams);
  next.delete(SECTION_TITLE_SEARCH_PARAM);
  next.delete(SECTION_LEVEL_SEARCH_PARAM);
  return next;
}

export function getPendingSectionScrollStorageKey(documentId: string): string {
  return `nb-pending-section-scroll-${documentId}`;
}

export function writePendingSectionScroll(documentId: string, section: PendingSectionScroll): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.setItem(getPendingSectionScrollStorageKey(documentId), JSON.stringify(section));
}

export function readPendingSectionScroll(documentId: string): PendingSectionScroll | null {
  if (typeof sessionStorage === 'undefined') {
    return null;
  }
  const raw = sessionStorage.getItem(getPendingSectionScrollStorageKey(documentId));
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as PendingSectionScroll;
    if (
      typeof parsed.title === 'string'
      && typeof parsed.level === 'number'
      && Number.isFinite(parsed.level)
    ) {
      return parsed;
    }
  } catch {
  }
  return null;
}

export function clearPendingSectionScroll(documentId: string): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.removeItem(getPendingSectionScrollStorageKey(documentId));
}

export function findTocHeadingPosition(
  headings: TocHeadingRef[],
  title: string,
  level: number,
): number | null {
  const exactMatches = headings.filter((heading) => heading.text === title && heading.level === level);
  if (exactMatches.length > 0) {
    return exactMatches[0].pos;
  }

  if (title === 'Document' && headings.length === 0) {
    return 0;
  }

  const titleMatches = headings.filter((heading) => heading.text === title);
  if (titleMatches.length === 1) {
    return titleMatches[0].pos;
  }

  return null;
}

export function findActiveTocItemPos(
  items: Array<{ pos: number }>,
  getItemTop: (pos: number) => number | null,
  viewportTop: number,
  viewportSlopPx = 0,
): number | null {
  if (items.length === 0) {
    return null;
  }

  const sortedItems = [...items].sort((left, right) => left.pos - right.pos);
  let activePos = sortedItems[0].pos;
  const activationLine = viewportTop + viewportSlopPx;

  for (const item of sortedItems) {
    const itemTop = getItemTop(item.pos);
    if (itemTop === null) {
      break;
    }
    if (itemTop <= activationLine) {
      activePos = item.pos;
      continue;
    }
    break;
  }

  return activePos;
}

export function findActiveTocItemPosAtDocPos(
  items: Array<{ pos: number }>,
  docPos: number,
): number | null {
  if (items.length === 0) {
    return null;
  }

  const sortedItems = [...items].sort((left, right) => left.pos - right.pos);
  let activePos = sortedItems[0].pos;

  for (const item of sortedItems) {
    if (item.pos <= docPos) {
      activePos = item.pos;
      continue;
    }
    break;
  }

  return activePos;
}

export const TOC_ACTIVE_VIEWPORT_OFFSET_PX = 80;
export const TOC_ACTIVE_VIEWPORT_SLOP_PX = 48;
